import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X, LogOut, PenLine } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const OwnerLogin = () => {
  const { isOwner, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(password);
      setOpen(false);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (isOwner) {
    return (
      <div className="flex items-center gap-2" data-testid="owner-mode-badge">
        <span
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs font-medium uppercase tracking-widest"
          style={{ background: 'rgba(207, 91, 29, 0.12)', color: 'var(--ochre)', border: '1px solid rgba(207, 91, 29, 0.3)' }}
        >
          <PenLine size={12} />
          Editing
        </span>
        <button
          onClick={logout}
          title="Log out of editing mode"
          data-testid="owner-logout-button"
          className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-black/5"
          style={{ color: 'var(--quiet)' }}
        >
          <LogOut size={16} />
        </button>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Owner login"
        data-testid="owner-login-button"
        className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:bg-black/5"
        style={{ color: 'var(--quiet)' }}
      >
        <Lock size={16} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(28, 26, 23, 0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-sm rounded-2xl p-8 shadow-2xl relative"
              style={{ background: 'var(--paper)', border: '1px solid var(--rule)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="owner-login-modal"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-quiet hover:text-ink transition-colors"
                data-testid="owner-login-close"
              >
                <X size={20} />
              </button>

              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(207, 91, 29, 0.1)' }}
              >
                <Lock size={22} style={{ color: 'var(--ochre)' }} />
              </div>
              <h3 className="font-display font-bold text-2xl mb-1" style={{ color: 'var(--ink)' }}>
                Owner login
              </h3>
              <p className="font-body text-sm text-quiet mb-6">
                Enter your password to edit diary entries and photos. Visitors see a read-only site.
              </p>

              <form onSubmit={handleSubmit}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoFocus
                  data-testid="owner-password-input"
                  className="w-full p-3 rounded-lg font-body text-base focus:outline-none focus:ring-2 mb-3"
                  style={{
                    background: 'rgba(255, 215, 186, 0.15)',
                    border: '1px solid var(--rule)',
                    color: 'var(--ink)',
                  }}
                />
                {error && (
                  <p className="font-body text-sm mb-3" style={{ color: '#b91c1c' }} data-testid="owner-login-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading || !password}
                  data-testid="owner-login-submit"
                  className="w-full py-3 rounded-full font-body font-medium uppercase tracking-widest text-sm transition-all hover:shadow-lg disabled:opacity-50"
                  style={{ background: 'var(--ochre)', color: 'var(--paper)' }}
                >
                  {loading ? 'Checking…' : 'Unlock editing'}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default OwnerLogin;
